import React, { useState, useEffect } from "react";
import { getItems, getParties, createSaleOrder } from "../../services/services";
import { toast } from "react-toastify";

const SaleOrderModal = ({ isOpen, onClose, existingOrder }) => {
  const [orderData, setOrderData] = useState({
    customerId: "",
    orderDate: "",
    items: [
      {
        productId: "",
        sellingQty: 0,
        sellingPrice: 0,
      },
    ],
    otherExpenses: [],
    receivedAmount: 0,
    paymentMode: "",
    notes: "",
    dueDate: "",
  });

  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchParties();
      fetchProducts();

      if (existingOrder) {
        setOrderData({
          customerId: existingOrder?.customerId || "",
          orderDate: existingOrder?.orderDate || "",
          items:
            existingOrder?.items?.map((item) => ({
              productId: item?.productId?._id || "",
              sellingQty: item?.quantity || 0,
              sellingPrice: item?.salePrice || 0,
            })) || [],
          otherExpenses: existingOrder?.otherExpenses || [],
          receivedAmount: existingOrder?.receivedAmount || 0,
          paymentMode: existingOrder?.paymentMode || "",
          notes: existingOrder?.notes || "",
          dueDate: existingOrder?.dueDate || "",
        });
      }
    }
  }, [isOpen, existingOrder]);

  const fetchParties = async () => {
    try {
      const res = await getParties();
      setParties(res?.data?.response?.parties || []);
    } catch (error) {
      console.error("Failed to fetch parties:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await getItems();
      setProducts(res?.data?.response?.products || []);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const handleItemChange = (index, field, value) => {
    setOrderData((prev) => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]:
          ["sellingQty", "sellingPrice"].includes(field) && value !== ""
            ? Number(value)
            : value,
      };
      return { ...prev, items: updatedItems };
    });
  };

  const handleOrderChange = (e) => {
    const { name, value } = e.target;
    setOrderData((prev) => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    setOrderData((prev) => ({
      ...prev,
      items: [...prev.items, { productId: "", sellingQty: 0, sellingPrice: 0 }],
    }));
  };

  const removeItem = (index) => {
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {   
      const payload = {
        ...orderData,
        customerId: orderData.customerId,
      };
      await createSaleOrder(payload);
      toast.success("Sale order saved successfully!");
      onClose();
    } catch (error) {
      toast.error("Error saving sale order");
      console.error(error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{
          maxWidth: "95vw",
          width: "95vw",
          height: "95vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="modal-content"
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            <div className="modal-header bg-custom text-white">
              <h5 className="modal-title">
                {existingOrder ? "Edit Sale Order" : "Create Sale Order"}
              </h5>
              <button
                type="button"
                className="btn"
                onClick={onClose}
                style={{
                  color: "#fff",
                  fontSize: "1.5rem",
                  background: "transparent",
                  border: "none",
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body" style={{ overflowY: "auto", flex: 1 }}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Party</label>
                  <select
                    name="customerId"
                    className="form-select"
                    value={orderData?.customerId || ""}
                    onChange={handleOrderChange}
                    required
                  >
                    <option value="">Select Party</option>
                    {parties.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Order Date</label>
                  <input
                    type="date"
                    name="orderDate"
                    className="form-control"
                    value={orderData?.orderDate || ""}
                    onChange={handleOrderChange}
                    required
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Sale Price (₹)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderData?.items?.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <select
                            className="form-select"
                            value={item?.productId || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "productId",
                                e.target.value
                              )
                            }
                            required
                          >
                            <option value="">Select Product</option>
                            {products.map((p) => (
                              <option key={p._id} value={p._id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={item?.sellingQty || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "sellingQty",
                                e.target.value
                              )
                            }
                            required
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            value={item?.sellingPrice || ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "sellingPrice",
                                e.target.value
                              )
                            }
                            required
                          />
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeItem(index)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  className="btn btn-primary btn-sm mt-2"
                  onClick={addItem}
                >
                  + Add Row
                </button>
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                zIndex: 10,
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem",
              }}
            >
              <button
                type="button"
                className="btn btn-custom"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-custom">
                Save Order
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaleOrderModal;
